import React, { useEffect, useRef, useState } from "react";
import useTranslate from "../../hooks/useTranslate";
import { CaretDown, CaretUp } from "./LoungeComposer";
import { ChannelMenuDesktop, ChannelSheetMobile } from "./ChannelPicker";

// Inline edit surface (QAL-T6, frame 1m·2): the card body turns into a
// composer in place — textarea with the focus ring, channel dropdown (posts
// only, plain trigger without the composer's dot, exactly as 1m·2 draws it),
// counter, Cancel + "Save changes". The hint line carries the workorder
// copy "Votes and comments are kept." (the drawn frame repeats the composer's
// no-club-names line in that slot — the workorder wording wins, divergence
// reported).
//
// Comment variant (design gap — no frame draws a comment edit; workorder
// sanctions the labels): same structure minus channel dropdown and minus the
// hint line, smaller textarea.
//
// Mobile is not drawn for 1m (gap): the layout follows the composer's own
// 1e/1k mobile rules — channel trigger as a full-width row, 44px controls.
//
// The component renders ONLY the editor column — callers keep their avatar
// column so the card's left edge never moves.

const MAX_LEN = 1000;

const LoungeInlineEdit = ({
  type = "post",
  initialText = "",
  channels = [],
  initialChannelSlug = null,
  submitting = false,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslate();
  const [text, setText] = useState(initialText);
  const [channelSlug, setChannelSlug] = useState(initialChannelSlug);
  const [pickerOpen, setPickerOpen] = useState(false);
  const textareaRef = useRef(null);
  const pickerRef = useRef(null);

  const isPost = type === "post";
  const selected = channels.find((c) => c.slug === channelSlug) || null;
  const canSave = text.trim().length > 0 && (!isPost || Boolean(selected)) && !submitting;

  // Focus with the caret at the end — the card just became this editor.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const save = () => {
    if (!canSave) return;
    onSave(
      isPost
        ? { text: text.trim(), channel: selected.slug }
        : { text: text.trim() },
    );
  };

  const trigger = (
    <>
      {selected ? selected.name : t("Choose a channel")}
      {pickerOpen ? <CaretUp /> : <CaretDown />}
    </>
  );

  return (
    <div
      className="flex-1 min-w-0 flex flex-col gap-[11px] sm:gap-3"
      data-testid={isPost ? "post-edit" : "comment-edit"}
    >
      <textarea
        ref={textareaRef}
        value={text}
        maxLength={MAX_LEN}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        aria-label={isPost ? t("Edit post") : t("Edit comment")}
        className={`w-full border border-[#DDE3EB] rounded-[10px] py-2.5 px-3 text-[14px] leading-[1.55] text-[#111827] resize-y outline-none focus:border-[#2283FF] focus:shadow-[0_0_0_3px_rgba(34,131,255,0.14)] ${
          isPost ? "min-h-[96px] sm:min-h-[74px]" : "min-h-[64px] sm:min-h-[56px]"
        }`}
        data-testid="edit-textarea"
      />
      {isPost && (
        <p className="m-0 -mt-[3px] sm:-mt-1 text-[11.5px] sm:text-[12px] text-[#9CA3AF]">
          {t("Votes and comments are kept.")}
        </p>
      )}

      {/* Mobile: channel selector as its own full-width row (composer 1e/1k
          rule — 1m draws no mobile edit). */}
      {isPost && (
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className={`sm:hidden flex items-center justify-between rounded-[10px] py-[11px] px-3 min-h-[44px] text-[13px] text-left ${
            selected ? "text-[#111827] font-medium" : "text-[#6B7280]"
          } ${
            pickerOpen
              ? "border border-[#2283FF] bg-white shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
              : "border border-[#DDE3EB] bg-white hover:border-[#C6CFDA]"
          }`}
          data-testid="edit-channel-trigger-mobile"
        >
          {trigger}
        </button>
      )}

      <div className="flex items-center gap-2.5 sm:gap-3">
        {/* Desktop: plain channel trigger inline in the controls row (1m·2 —
            current channel name + caret, no dot). */}
        {isPost && (
          <div ref={pickerRef} className="relative hidden sm:block">
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className={`inline-flex items-center gap-2 rounded-[10px] py-[7px] px-3 text-[13px] whitespace-nowrap bg-white border border-[#DDE3EB] hover:border-[#C6CFDA] ${
                selected ? "text-[#111827]" : "text-[#6B7280]"
              }`}
              data-testid="edit-channel-trigger"
            >
              {trigger}
            </button>
            {pickerOpen && (
              <ChannelMenuDesktop
                channels={channels}
                value={channelSlug}
                onSelect={(slug) => {
                  setChannelSlug(slug);
                  setPickerOpen(false);
                }}
              />
            )}
          </div>
        )}

        <span className="flex-1" />
        <span
          className={
            text.length >= MAX_LEN
              ? "text-[12.5px] font-bold text-[#111827]"
              : "text-[12.5px] text-[#9CA3AF]"
          }
          data-testid="edit-counter"
        >
          {text.length} / {MAX_LEN}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="bg-white border border-[#DDE3EB] rounded-[10px] py-0 px-4 h-11 sm:h-auto sm:py-[9px] text-[13.5px] font-semibold text-[#6B7280] hover:border-[#C6CFDA] hover:text-[#111827] whitespace-nowrap"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className={`bg-[#2283FF] text-white border-none rounded-[10px] py-0 px-5 h-11 sm:h-auto sm:py-[9px] text-[13.5px] font-semibold whitespace-nowrap ${
            canSave ? "hover:bg-[#1B6FE0]" : "opacity-50 cursor-default"
          }`}
          data-testid="edit-save"
        >
          {t("Save changes")}
        </button>
      </div>

      {isPost && selected && (
        <p className="m-0 -mt-0.5 text-[12.5px] text-[#6B7280]">
          <span className="font-semibold text-[#111827]">{selected.name}:</span>{" "}
          {selected.description}
        </p>
      )}

      {isPost && pickerOpen && (
        <ChannelSheetMobile
          channels={channels}
          value={channelSlug}
          onSelect={(slug) => {
            setChannelSlug(slug);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default LoungeInlineEdit;
